import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import AdminUsers from './components/AdminUsers';
import AdminOrders from './components/AdminOrders';
import AdminProducts from './components/AdminProducts';
import EditProduct from './components/EditProduct';
import OrderStatus from './components/OrderStatus';
import UserOrders from './components/UserOrders';
import ProfileUpdate from './components/ProfileUpdate';
import Checkout from './components/Checkout';
import CartSync from './components/CartSync';
import ToastNotification from './components/ToastNotification';
import PublicOrderStatus from './components/PublicOrderStatus';
import PublicProduct from './components/PublicProduct';
import OrderSuccess from './components/OrderSuccess';
import Payment from './components/Payment';
import Wishlist from './components/Wishlist';
import Layout from './components/Layout';
import NotificationsPage from './components/NotificationsPage';
import ProductDetails from './components/ProductDetails';
import AboutUs from './components/AboutUs';
import ContactUs from './components/ContactUs';
import AdminAppUpdates from './components/AdminAppUpdates';
import LandingPage from './components/LandingPage';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import NoRefundPolicy from './components/NoRefundPolicy';
import InvoicePage from './components/InvoicePage';
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuth } from './contexts/AuthContext';
import { Navigate } from 'react-router-dom';

const RootRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
};

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <AuthProvider>
          <CartProvider>
            <NotificationProvider>
          <CartSync />
          <ToastNotification />
          <Router>
            <div className="App">
              <Routes>
                <Route path="/" element={<RootRoute />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/order/success/:id" element={<OrderSuccess />} />
                <Route 
                  path="/order/pay/:id" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Payment />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                {/* Public shareable routes */}
                <Route path="/share/orders/:id" element={<Layout><PublicOrderStatus /></Layout>} />
                <Route path="/share/products/:id" element={<Layout><PublicProduct /></Layout>} />
                <Route 
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <NotificationsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/checkout" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Checkout />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/orders" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UserOrders />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/orders/:id" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <OrderStatus />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/orders/:id/invoice" 
                  element={
                    <ProtectedRoute>
                      <InvoicePage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/wishlist" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Wishlist />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/products/:id" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ProductDetails />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/users" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <Layout>
                        <AdminUsers />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/orders" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <Layout>
                        <AdminOrders />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/products" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <Layout>
                        <AdminProducts />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/products/:id/edit" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <Layout>
                        <EditProduct />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/app-updates" 
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <Layout>
                        <AdminAppUpdates />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ProfileUpdate />
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/about" 
                  element={
                    <Layout>
                      <AboutUs />
                    </Layout>
                  } 
                />
                <Route 
                  path="/contact" 
                  element={
                    <Layout>
                      <ContactUs />
                    </Layout>
                  } 
                />
                <Route 
                  path="/terms-and-conditions" 
                  element={
                    <Layout>
                      <TermsAndConditions />
                    </Layout>
                  } 
                />
                <Route 
                  path="/privacy-policy" 
                  element={
                    <Layout>
                      <PrivacyPolicy />
                    </Layout>
                  } 
                />
                <Route 
                  path="/no-refund-policy" 
                  element={
                    <Layout>
                      <NoRefundPolicy />
                    </Layout>
                  } 
                />
              </Routes>
            </div>
          </Router>
          </NotificationProvider>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  </HelmetProvider>
  );
}

export default App;
